import { useTranslation } from 'react-i18next';

import { KB_SIZE_CEILING } from '@/hooks/useKbProfileForm';
import type { KnowledgeSize } from '@/services/kbProfileService';

interface KbSizeIndicatorProps {
  /** Authoritative size from the backend (`knowledge_size` on the profile). */
  size?: KnowledgeSize | null;
  /** Live wizard estimate while typing (pre-save) — overrides `size.chars`
   * until the next save reconciles with the real assembled value. */
  estimatedChars?: number;
}

/**
 * F074 (v2) — context-budget indicator fed by the REAL assembled
 * `knowledge_md` size from the backend; `estimateKbChars()` survives only as
 * the wizard's live pre-save feedback. Deliberately discreet and monochrome
 * (border + foreground/30 fill, no alarm states): the per-field caps make
 * overflowing the ceiling structurally impossible, so this educates
 * ("dense beats long", §9) rather than warns.
 */
export function KbSizeIndicator({ size, estimatedChars }: KbSizeIndicatorProps) {
  const { t } = useTranslation();
  const ceiling = size?.ceiling ?? KB_SIZE_CEILING;
  const chars = estimatedChars ?? size?.chars ?? 0;
  const pct = Math.min(100, Math.round((chars / ceiling) * 100));

  return (
    <div className="text-xs text-muted-foreground space-y-1" data-testid="kb-size-indicator">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1 bg-border rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-foreground/30 transition-all duration-300"
            style={{ width: `${pct}%` }}
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t('onboarding.kbForm.size_indicator_label')}
          />
        </div>
        <span>{t('onboarding.kbForm.size_indicator_text', { chars, ceiling })}</span>
      </div>
      <p>{t('onboarding.kbForm.size_indicator_hint')}</p>
    </div>
  );
}
